import { useCallback, useState } from "react";

import { useMutation } from "@apollo/client";
import { useIntl } from "react-intl";

import { useBlizzardEvents } from "../../analytics/hooks";
import { useLensSubmissionRoutesV3 } from "../../core/routes/hooks";
import {
    CofConfigId,
    ExclusiveLensStatus,
    MyLensesLensCtaText,
    MyLensesLensCtaType,
    SurfaceType,
    Visibility,
} from "../../gql-generated/types";
import { getLens } from "../../lens/api/queries";
import { LENS_DESCRIPTION_MAX_CHARS } from "../../lens/components/constants";
import { makeLensCtaToSubmit } from "../../lens-cta/models";
import { makeLensDiscoverabilityInput } from "../../lens-discoverability/hooks";
import { useSelectedLensFolder, useSelectedLensFolderV3, useSubmissionQuotas } from "../../shared/hooks";
import { type ApolloResult } from "../../shared/models";
import { mapPreviewMediaItemsToResources } from "../../shared/state";
import {
    type CreateLensSpaceLensMutation,
    type CreateLensSpaceLensMutationVariables,
    createLensSpaceLens,
} from "../api/mutation";
import { type GetAllPreSubmissionInfoWithOrgResourcesQuery } from "../api/queries";
import { type GetPreSubmissionInfoQuery } from "../api/queries/getPreSubmissionInfo.gql-generated";
import { getLensPublicationErrorMessage } from "../utils/publicationError";
import { validateExclusiveLens } from "../validation/exclusiveLensValidation";

import { messages } from "./messages.generated";
import { useLensDetails } from "./useLensDetails";
import { useLensForUpdate } from "./useLensForUpdate";
import { useLensStatusCheck } from "./useLensStatusCheck";
import { useSubmitLensConfiguration } from "./useSubmitLensConfiguration";
import { useSubmittingLens } from "./useSubmittingLens";

import { useFeatureGates } from "src/app/cof-feature-gates/hooks";

export const useLensSpaceLens = (
    lensId: string,
): ApolloResult<{
    data: GetAllPreSubmissionInfoWithOrgResourcesQuery | GetPreSubmissionInfoQuery | undefined;
    submitLens: (overrideVisibility?: Visibility, overrideLensCreatorPayoutEnrolled?: boolean) => Promise<void>;
    submitLoading: boolean;
    errorMessage: string | null;
}> => {
    const intl = useIntl();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const { sendBlizzardEvent } = useBlizzardEvents();
    const { data, loading, error, submissionId } = useSubmittingLens();
    const {
        tagsList,
        visibility,
        lensCta,
        lensPreviewMediaItems,
        isGameUserProvided,
        discoverability,
        lensCreatorPayoutEnrolled,
        description,
        resetDetails,
        releaseNote,
        spectaclesTagsList,
    } = useLensDetails(submissionId ?? lensId);

    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;
    const submissionQuotasEnabled = configs[CofConfigId.MyLensesSubmissionQuotas].enabled;

    const selectedLensFolderV3 = useSelectedLensFolderV3();
    const { lensFolder: selectedLensFolder } = useSelectedLensFolder();
    const { submitLensState } = useSubmitLensConfiguration();
    const { refetch: refetchSubmissionQuotas } = useSubmissionQuotas();

    const { goToLensCreateSuccess } = useLensSubmissionRoutesV3();

    const [createLens] = useMutation<CreateLensSpaceLensMutation, CreateLensSpaceLensMutationVariables>(
        createLensSpaceLens,
    );

    const [submitLoading, setSubmitLoading] = useState(false);

    const { checkLensStatus } = useLensStatusCheck(lensId);
    const { data: lensForUpdateData } = useLensForUpdate();

    const submitLens = useCallback(
        async (overrideVisibility?: Visibility, overrideLensCreatorPayoutEnrolled?: boolean) => {
            setSubmitLoading(true);
            setErrorMessage(null);

            if (!data) {
                throw new Error("No lens data to submit.");
            }

            const {
                getPreSubmissionInfo: { name, analytics, surfaceType, hasMusic, hasLyrics, isAIClip },
            } = data;

            const selectedLensFolderId = isMyLensesOrgsV2Enabled ? selectedLensFolder?.id : selectedLensFolderV3?.id;
            const requestedVisibility = overrideVisibility ?? visibility;
            const lensForUpdate = lensForUpdateData?.getLens.lens;
            const isNewLensPublication =
                !lensForUpdate && requestedVisibility !== Visibility.VisibilityDontPublishToSnapchat;

            try {
                await checkLensStatus();

                // Check if this is an exclusive lens update that fails validation
                const isExclusiveLens =
                    lensForUpdate?.exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusExclusive;
                const isExclusiveLensPending =
                    lensForUpdate?.exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusEligible;

                if (lensForUpdate && (isExclusiveLens || isExclusiveLensPending)) {
                    const validation = validateExclusiveLens({
                        hasMusic: hasMusic || false,
                        hasLyrics: hasLyrics || false,
                        previewMediaItems: lensPreviewMediaItems,
                        visibility: requestedVisibility,
                    });

                    if (!validation.isValid) {
                        setErrorMessage(`Cannot submit exclusive lens: ${validation.errorMessage}`);
                        return;
                    }
                }

                if (isAIClip) {
                    const validation = validateExclusiveLens({
                        hasMusic,
                        hasLyrics,
                        previewMediaItems: lensPreviewMediaItems,
                        visibility: requestedVisibility,
                    });

                    if (!validation.isValid) {
                        setErrorMessage(`Cannot submit AI Clip Lens: ${validation.errorMessage}`);
                        return;
                    }
                }

                const previewMediaResources = mapPreviewMediaItemsToResources(lensPreviewMediaItems);
                const discoverabilityInput = makeLensDiscoverabilityInput(discoverability);
                const truncatedDescription = description.slice(0, Math.max(0, LENS_DESCRIPTION_MAX_CHARS));

                const { data } = await createLens({
                    variables: {
                        visibility: requestedVisibility,
                        lensId,
                        tagsList,
                        lensName: name,
                        submissionId: submissionId ?? "",
                        previewMediaResources,
                        description: surfaceType.includes(SurfaceType.SurfaceTypeSpectacles)
                            ? truncatedDescription
                            : "",
                        lensCta: makeLensCtaToSubmit({
                            type: lensCta?.type ?? MyLensesLensCtaType.CtaTypeUnset,
                            text: lensCta?.text ?? MyLensesLensCtaText.CtaTextUnset,
                            website: {
                                url: lensCta?.website?.url ?? "",
                            },
                            deeplink: {
                                uri: lensCta?.deeplink?.uri ?? "",
                                appName: lensCta?.deeplink?.appName ?? "",
                                iosAppId: lensCta?.deeplink?.iosAppId ?? 0,
                                androidAppUrl: lensCta?.deeplink?.androidAppUrl ?? "",
                            },
                        }),
                        digitalGoodsList: [],
                        discoverability: discoverabilityInput,
                        lensSpaceId: selectedLensFolderId ?? "",
                        lensCreatorPayoutEnrolled: overrideLensCreatorPayoutEnrolled ?? lensCreatorPayoutEnrolled,
                        releaseNote,
                        spectaclesTagsList,
                        isMarkedExclusive: submitLensState.isExclusiveLens,
                        isGameUserProvided,
                    },
                    // Only refetch a lens that exists. On the create path there is no lens yet
                    // (the caller passes `lensId ?? ""`), and Apollo runs refetchQueries
                    // unconditionally on success — so an unguarded entry here sent
                    // getLens(lensId: "") on every publication and the server rejected it with
                    // INVALID_ARGUMENT "Missing lens_id.". Same shape as useSetLensCta,
                    // useSetDiscoverability and useSetLensPreview.
                    refetchQueries: lensId ? [{ query: getLens, variables: { lensId } }] : [],
                });

                if (!data) {
                    throw new Error("Lens data missing from response.");
                }

                const {
                    createMyLensesLensSpaceLens: { lens },
                } = data;

                if (analytics) {
                    sendBlizzardEvent({
                        event: "LENSSTUDIO_MYLENSES_SUBMIT",
                        analytics,
                        lensId: lens.id,
                        lensSubmissionType: "LENS_SUBMISSION_LENS_FOLDER",
                    });
                }

                resetDetails();

                goToLensCreateSuccess(submissionId ?? "", lens.id);
            } catch (error: unknown) {
                setErrorMessage(
                    getLensPublicationErrorMessage(error, {
                        fallback: intl.formatMessage(messages.submissionFailed),
                        communityDistributionDenied: intl.formatMessage(messages.communityDistributionDenied),
                    }),
                );
            } finally {
                setSubmitLoading(false);

                if (isNewLensPublication && submissionQuotasEnabled) {
                    void Promise.resolve()
                        .then(refetchSubmissionQuotas)
                        .catch(() => {
                            // Quota reads fail open; final enforcement remains authoritative in Lens Central.
                        });
                }
            }
        },
        [
            data,
            isMyLensesOrgsV2Enabled,
            submissionQuotasEnabled,
            selectedLensFolder?.id,
            selectedLensFolderV3?.id,
            checkLensStatus,
            lensForUpdateData?.getLens.lens,
            lensPreviewMediaItems,
            isGameUserProvided,
            discoverability,
            description,
            createLens,
            visibility,
            lensId,
            tagsList,
            submissionId,
            lensCta?.type,
            lensCta?.text,
            lensCta?.website?.url,
            lensCta?.deeplink?.uri,
            lensCta?.deeplink?.appName,
            lensCta?.deeplink?.iosAppId,
            lensCta?.deeplink?.androidAppUrl,
            lensCreatorPayoutEnrolled,
            releaseNote,
            spectaclesTagsList,
            submitLensState.isExclusiveLens,
            resetDetails,
            goToLensCreateSuccess,
            sendBlizzardEvent,
            refetchSubmissionQuotas,
            intl,
        ],
    );

    return { submitLens, submitLoading, data, error, loading, errorMessage };
};
